<template>
    <div class="vx-radial-graph">
        <vx-select
            v-if="!isSingleSeries"
            v-model="localSeries"
            :options="localPercentageSeries"
            text-key="name"
            id-key="name"
        />
        <vx-graph
            v-bind="$attrs"
            v-on="listeners"
            v-model="localValue"
            ref="chart"
            type="radialBar"
            value-format="percentage"
            :config="chartOptions"
            :show-data-labels="fullInterface"
            :full-interface="fullInterface"
            :series="[localSeries]"
            @dataPointSelection="updateLabels"
        >
            <template #tooltip="props" v-if="$scopedSlots.tooltip">
                <slot name="tooltip" v-bind="props" />
            </template>
        </vx-graph>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import VueApexCharts from 'vue-apexcharts'
import _color from '@/assets/utils/color.js'
import chartMixins from './chartMixins.js'

export default {
    name: 'vx-radial-graph',
    mixins: [chartMixins],
    components: {
        VueApexCharts
    },
    props: {
        value: {
            type: [Object, Number]
        },
        fullInterface: {
            type: Boolean,
            default: true
        },
        goal: {
            type: Number
        }
    },
    watch: {
        value: {
            handler: function (newV) {
                if (!newV) {
                    return
                }
                var newId = this.getIdForDataPoint(newV)
                var seriesIndex = this.series.findIndex((series) => {
                    return !!series.data.first((dataPoint) => {
                        return this.getIdForDataPoint(dataPoint) == newId
                    })
                })

                if (seriesIndex >= 0) {
                    this.selectedSeriesIndex = seriesIndex
                }
            },
            immediate: true
        }
    },
    data() {
        return {
            selectedSeriesIndex: 0,
            fontFamily: '"Montserrat", Helvetica, Arial, sans-serif'
        }
    },
    computed: {
        ...mapGetters({
            themeMode: 'user/themeMode'
        }),
        localValue: {
            get() {
                if (!this.value) {
                    return null
                }

                var valueId = this.getIdForDataPoint(this.value)
                var newValue = null
                this.localPercentageSeries.first((series) => {
                    return series.data.first((dataPoint) => {
                        var dataPointId = this.getIdForDataPoint(dataPoint.original)
                        if (dataPointId == valueId) {
                            newValue = dataPoint
                            return true
                        }
                    })
                })
                return newValue
            },
            set(dataPoint) {
                this.$emit('input', dataPoint && dataPoint.original)
            }
        },
        localPercentageSeries() {
            var seriesList = []

            this.series.forEach((series) => {
                var max = series.data.max((dataPoint) => dataPoint.value)

                var seriesData = series.data.map((dataPoint) => {
                    var goal = dataPoint.goal || this.goal || max
                    var value = dataPoint.value

                    if (goal != null) {
                        value = (dataPoint.value / goal) * 100
                    }
                    return {
                        name: dataPoint.name,
                        value: value.round(0),
                        original: dataPoint
                    }
                })

                seriesList.push({
                    name: series.name,
                    data: seriesData
                })
            })
            return seriesList
        },
        localSeries: {
            get() {
                if (!this.localPercentageSeries || !this.localPercentageSeries.length) {
                    return []
                }
                return this.localPercentageSeries[this.selectedSeriesIndex || 0]
            },
            set(v) {
                var newIndex = this.localPercentageSeries.indexOf(v)
                if (newIndex == -1) {
                    newIndex = 0
                }
                this.selectedSeriesIndex = newIndex
            }
        },
        chartOptions() {
            return {
                legend: {
                    position: 'right',
                    show: this.fullInterface,
                    floating: false,
                    markers: {
                        offsetY: 2
                    }
                },
                tooltip: {
                    theme: 'dark'
                },
                plotOptions: {
                    radialBar: {
                        endAngle: this.fullInterface ? 270 : 360,
                        inverseOrder: false,
                        hollow: {
                            margin: 5,
                            size: '10%',
                            background: 'transparent',
                            position: 'front'
                        },
                        animations: {
                            enabled: false // Disable radial bar specific animations
                        },
                        track: {
                            show: true,
                            startAngle: 0,
                            endAngle: this.fullInterface ? 270 : 360,
                            background: this.themeMode == 'light' ? '#010101' : '#f1f1f1',
                            strokeWidth: '100%',
                            opacity: 0.25,
                            margin: 3,
                            dropShadow: {
                                enabled: false,
                                top: 0,
                                left: 0,
                                blur: 3,
                                opacity: 0.5
                            }
                        },
                        dataLabels: {
                            show: this.fullInterface,
                            background: {
                                enabled: true,
                                foreColor: '#fff',
                                padding: 4,
                                borderRadius: 2,
                                borderWidth: 1,
                                borderColor: '#fff'
                            },
                            name: {
                                show: true,
                                // offsetY: -100,
                                // offsetX: 40,
                                fontSize: '22px',
                                fontFamily: this.fontFamily,
                                fontWeight: 600
                            },
                            value: {
                                show: true,
                                fontSize: '22px',
                                fontWeight: 'bold',
                                fontFamily: this.fontFamily,
                                // offsetY: -8,
                                formatter: function (val) {
                                    return val + '%'
                                }
                            }
                        }
                    }
                }
            }
        },
        listeners() {
            var handlers = {
                ...this.$listeners
            }
            delete handlers.input
            return handlers
        }
    },
    methods: {
        formatValue(value, config) {
            return this.$refs.chart.formatValue(Number(value))
        },
        updateLabels(selectedDataPoint) {
            selectedDataPoint = selectedDataPoint || this.localValue
            if (!selectedDataPoint) {
                return
            }

            this.setCenterLabel(selectedDataPoint.name)
            this.setCenterLabelValue(selectedDataPoint.value)

            var group = this.$el.querySelector('.apexcharts-datalabels-group')

            if (group) {
                group.style.opacity = 1
            }
        },
        setCenterLabel(text) {
            var label = this.$el.querySelector('.apexcharts-text.apexcharts-datalabel-label')
            if (label) {
                label.innerHTML = text
            }
        },
        setCenterLabelValue(text) {
            var value = this.$el.querySelector('.apexcharts-text.apexcharts-datalabel-value')
            if (value) {
                value.innerHTML = this.formatValue(text)
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.theme-dark {
    .vx-radial-graph {
        ::v-deep .apexcharts-radialbar-area {
            // &:hover,
            &[selected='true'] {
                stroke: white;
            }
        }
    }
}
.vx-radial-graph {
    ::v-deep {
        .apexcharts-datalabels-group {
            transform: translateY(-28%) translateX(-15%);
        }

        .apexcharts-radialbar-area {
            // &:hover,
            &[selected='true'] {
                fill-opacity: 1;
                stroke-width: 3;
                stroke: black;
                filter: none;
            }
        }
    }
}
</style>
